import { ScrollablePane, Stack, mergeStyles } from '@fluentui/react';
import * as React from 'react';
import { Suspense } from 'react';
import { FullSpinner } from '../../../../web/common/components/FullSpinner';
import { appContext } from '../../../../web/models/getHelp/appContext';
import { getContentRootStyles, getContentScrollablePaneStyles } from '../../../../web/self-help/styles/selfHelpStyles';
import { getHelpLogger } from '../../../../web/utils/getHelpLogger';
import { ContactFormBrand } from '../../../models/apiModels';
import { SourceApps } from '../../../models/constants/constants';
import { IGlobalState, IUITabViewInfo, UITabView, UITabViewInfo, UITabViewMode } from '../../../models/reduxState';
import { isBranded } from '../../../utilities';
import '../../common/css/contactUs.scss';
import '../../common/css/help.scss';
import { Region } from '../models/modalityModels';
import {
  getCommonClassNames,
  getCommonStyles,
  getCommonThemedClassNames,
  getCommonThemedStyles,
} from '../styles/commonStyles';

import { getContactUsClassNames, IContactUsStyles } from '../styles/contactUsStyles';
import { IStyledComponent } from '../styles/themesUI';
import { getString, isRtl } from '../../../../web/utils/stringLoader';
import { collectTelemetry } from '../../../../web/utils/oneDsUtil';
import { LogType, LogHint } from '../../../../web/models/emeraldLoggingRequest'; 

/* eslint-disable promise/prefer-await-to-then */

const ContactUsBody = React.lazy(() => import('./ContactUsBody'));
const NavBar = React.lazy(() => import('./NavBar'));
const IframeContainer = React.lazy(() =>
  import('../containerComponents/IframeContainer').then(module => ({ default: module.IframeContainer }))
);
const BrandedHeaderContainer = React.lazy(() =>
  import('../containerComponents/BrandedHeaderContainer').then(module => ({
    default: module.BrandedHeaderContainer,
  }))
);
const ContactUsFooterContainer = React.lazy(() =>
  import('../containerComponents/ContactUsFooterContainer').then(module => ({
    default: module.ContactUsFooterContainer,
  }))
);
const NavigationContainer = React.lazy(() =>
  import('../containerComponents/NavigationContainer').then(module => ({ default: module.NavigationContainer }))
);
const AppBar = React.lazy(() =>
  import('../../../../web/common/components/AppBar').then(module => ({ default: module.AppBar }))
);
const InAppReader = React.lazy(() =>
  import('../../../../web/common/components/InAppReader').then(module => ({ default: module.InAppReader }))
);
export interface ContactUsProps {
  inArticle: boolean;
  articleUrl: string;
  backButtonDisabled: boolean;
  runningInSoc: boolean;
  globalState: IGlobalState;
  hideSelfHelpSmcIvr: boolean;
  uITabViewStack: IUITabViewInfo[];
  showModalitiesStickyFooter: boolean;
  isOutlookDotCom: boolean;
  appName: string;
  sourceApp: SourceApps;
  isThirdParty: boolean;
  isAgave: boolean;
  isNonAgave: boolean;
  isTeams: boolean;
  regionInfo: Array<Region>;
  isAmc: boolean;
  isGetHelp: boolean;
  showAppBar: boolean;
  useAnnouncementFloat: boolean;
  shouldResetFocus: boolean;
  showWindowsUpdateAvailableForSASelfHelp: boolean;
  showWindowsUpdateAvailableForSAModalities: boolean;
  showEndOfLifeMessageForSASelfHelp: boolean;
  showEndOfLifeMessageForSAModalities: boolean;
  showWin10EndOfSupportMessage: boolean;
  isSmbHub: boolean;

  // dispatchers
  dispatchGetRegionInfo: () => void;
  dispatchUserInfoFetch: () => void;
  dispatchGetAlchemyThemeRoutingData: () => Promise<void>;
  dispatchBypassThemeRouting: (value: string) => void;
  popHistory: () => Record<string, unknown>;
  resetContactUsFocus: (value: boolean) => void;
  updateUITabViewInfo: (uiTabViewInfo: IUITabViewInfo) => void;
}

// Assumptions made with Contact Us reSkin
// Always show back button
// Not implemented isFromFeedback conditionals (line 49 chat.cshtml)
// Not hit from feedback or client referral (line 272 homeController)
// InstantBCQR_ENabled.currentvalue = true (line 55 chat.cshtml)

type IContactUsAppProps = ContactUsProps & IStyledComponent<IContactUsStyles>;

export class ContactUsApp extends React.Component<IContactUsAppProps, any> {
  public componentDidMount() {
    getHelpLogger.logLanding();
    collectTelemetry({ ContentId: 'ContactUsAppMounted', Type: LogType.ContentUpdate, Hint: LogHint.ContactUsAppMounted });
    if (!this.props.regionInfo) {
      this.props.dispatchGetRegionInfo();
    }
  }

  public render() {
    const containerDirAttr = isRtl() ? 'rtl' : 'ltr';
    const commonClassNames = getCommonClassNames(getCommonStyles);
    const commonThemedClassnames = getCommonThemedClassNames(
      getCommonThemedStyles,
      { theme: this.props.theme }
    );
    const contactUsClassNames = getContactUsClassNames(this.props.styles, { theme: this.props.theme, sourceApp: this.props.sourceApp, isTeams: this.props.isTeams, isNonAgave: this.props.isNonAgave });

    /**
     * If the application is brandable, then the Branded Header container must be rendered in addition to the body.
     */
    if (isBranded(this.props.sourceApp || this.props.globalState.appVer)) {
      return (
        <div className={contactUsClassNames.fullHeightContainer} dir={containerDirAttr}>
            {this.renderBrandedContactUsBody()}
          </div>);
    }

    return (
      <Suspense fallback={null}>
        {this.props.isGetHelp ? (
          <>
            <AppBar theme={this.props.theme} />
            <ScrollablePane
              id="GetHelpScrollablePane"
              styles={getContentScrollablePaneStyles(this.props.theme, this.props.showAppBar)}
            >
              <Stack styles={getContentRootStyles(this.props.inArticle)}>
                {this.props.inArticle ? this.renderArticle() : this.renderContactUsBody()}
              </Stack>
              <ContactUsFooterContainer />
            </ScrollablePane>
          </>
        ) : (
          <div
            className={
              this.props.sourceApp === SourceApps.SurfaceApp || this.props.sourceApp === SourceApps.SurfaceApp2 || this.props.isTeams
                ? commonClassNames.contentAndFooterWrapper
                : ''
            }
          >
            <div
              className={mergeStyles(
                contactUsClassNames.containerFluid,
                contactUsClassNames.fullHeightContainer,
                this.props.sourceApp === SourceApps.SurfaceApp || this.props.sourceApp === SourceApps.SurfaceApp2 ||this.props.isTeams
                  ? mergeStyles(commonThemedClassnames.surfaceAppScrollBar, commonClassNames.overflowAuto)
                  : ''
              )}
              dir={containerDirAttr}
            >
              {this.props.inArticle ? this.renderArticle() : this.renderContactUsBody()}
            </div>
            {!this.props.isThirdParty && <ContactUsFooterContainer />}
          </div>
        )}
      </Suspense>
    );
  }

  private ocvSupportLinkCallback = (isGetHelp: boolean, updateUITabViewInfo: Function, resetContactUsFocus: Function, dispatchGetAlchemyThemeRoutingData: Function, dispatchBypassThemeRouting: Function, viewMode?: UITabViewMode) => {
    const uiTabViewInfo = new UITabViewInfo();
    uiTabViewInfo.uITabView = UITabView.requestCreation;
    if (viewMode) {
      uiTabViewInfo.viewMode = viewMode;
    }
    uiTabViewInfo.showAppBarBackButton = isGetHelp;
    dispatchGetAlchemyThemeRoutingData()
    dispatchBypassThemeRouting('');
    updateUITabViewInfo(uiTabViewInfo);
    resetContactUsFocus(true);
  }

  private renderArticle = (): JSX.Element => {
    return this.props.isGetHelp ? (
      <InAppReader />
    ) : (
      <div>
        <NavBar
          backButtonAriaLabel={getString("navBar_backButtonAriaLabel")}
          displayNamespace={this.props.globalState.displayNamespace}
          inArticle={this.props.inArticle}
          popHistory={this.props.popHistory}
          backButtonDisabled={this.props.backButtonDisabled}
        />
        <IframeContainer articleUrl={this.props.articleUrl} />
      </div>
    );
  };

  private renderContactUsBody = (): JSX.Element => {
    const commonClassNames = getCommonClassNames(getCommonStyles);
    const body = <ContactUsBody
      sourceApp={this.props.sourceApp}
      isAgave={this.props.isAgave}
      hideSelfHelpSmcIvr={this.props.hideSelfHelpSmcIvr}
      isOutlookDotCom={this.props.isOutlookDotCom}
      uITabViewStack={this.props.uITabViewStack}
      showModalitiesStickyFooter={this.props.showModalitiesStickyFooter}
      isThirdParty={this.props.isThirdParty}
      isAmc={this.props.isAmc}
      useAnnouncementFloat={this.props.useAnnouncementFloat}
      shouldResetFocus={this.props.shouldResetFocus}
      resetContactUsFocus={this.props.resetContactUsFocus}
      showWindowsUpdateAvailableForSASelfHelp={this.props.showWindowsUpdateAvailableForSASelfHelp}
      showWindowsUpdateAvailableForSAModalities={this.props.showWindowsUpdateAvailableForSAModalities}
      showEndOfLifeMessageForSASelfHelp={this.props.showEndOfLifeMessageForSASelfHelp}
      showEndOfLifeMessageForSAModalities={this.props.showEndOfLifeMessageForSAModalities}
      showWin10EndOfSupportMessage={this.props.showWin10EndOfSupportMessage}
      isSmbHub={this.props.isSmbHub}
    />;
    return this.props.isGetHelp ? (
      <Stack>
        {body}
        {appContext.appSupportsConfigUpdate && !this.props.globalState.getHelpConfig && <FullSpinner />}
      </Stack>
    ) : (
      <div className={commonClassNames.row}>
        <div className="col-lg-3 col-xs-0" />
        <div className="col-lg-7 col-xs-12">
          <NavigationContainer />
          {body}
        </div>
      </div>
    );
  };

  private renderBrandedContactUsBody = (): JSX.Element => {
    const { appName } = this.props;
    const isGroupMe = appName.indexOf(ContactFormBrand.GroupMe) > -1;
    const contactUsClassNames = getContactUsClassNames(this.props.styles, { theme: this.props.theme, sourceApp: this.props.sourceApp });
    const commonClassNames = getCommonClassNames(getCommonStyles);
    return (
      <div id="brandedHeader">
        {!isGroupMe && (
          <div className={contactUsClassNames.containerFluid}>
            <div className={commonClassNames.row}>
              <div className="col-lg-3 col-xs-0" />
              <div className="col-lg-7 col-xs-12">
                <NavigationContainer />
              </div>
            </div>
          </div>
        )}
        <BrandedHeaderContainer />
        <div className={contactUsClassNames.containerFluid}>
          <div className={commonClassNames.row}>
            <div className="col-lg-3 col-xs-0" />
            <div className="col-lg-7 col-xs-12">
              {isGroupMe && <NavigationContainer />}
              <ContactUsBody
                sourceApp={this.props.sourceApp}
                isAgave={this.props.isAgave}
                hideSelfHelpSmcIvr={this.props.hideSelfHelpSmcIvr}
                isOutlookDotCom={this.props.isOutlookDotCom}
                uITabViewStack={this.props.uITabViewStack}
                showModalitiesStickyFooter={
                  this.props.showModalitiesStickyFooter
                }
                isThirdParty={this.props.isThirdParty}
                isAmc={this.props.isAmc}
                useAnnouncementFloat={this.props.useAnnouncementFloat}
                shouldResetFocus={this.props.shouldResetFocus}
                resetContactUsFocus={this.props.resetContactUsFocus}
                showWindowsUpdateAvailableForSASelfHelp={this.props.showWindowsUpdateAvailableForSASelfHelp}
                showWindowsUpdateAvailableForSAModalities={this.props.showWindowsUpdateAvailableForSAModalities}
                showEndOfLifeMessageForSASelfHelp={this.props.showEndOfLifeMessageForSASelfHelp}
                showEndOfLifeMessageForSAModalities={this.props.showEndOfLifeMessageForSAModalities}
                showWin10EndOfSupportMessage={this.props.showWin10EndOfSupportMessage}
                isSmbHub={this.props.isSmbHub}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };
}
